// src/pages/Admin/CoursAgenda/components/CoursAgendaFormDrawer.jsx

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FaCalendarAlt,
  FaClock,
  FaMapMarkerAlt,
  FaSave,
  FaUser,
  FaUserFriends,
} from "react-icons/fa";

import CoursAgendaDrawerShell from "./CoursAgendaDrawerShell";

import {
  addDaysToYmd,
  getPersonName,
  toDateInputValue,
  toTimeInputValue,
  ymdLocal,
} from "../utils/coursAgendaUtils";

const CONTROL_CLASS = `
  cours-agenda-control
  w-full
  rounded-xl
  border border-slate-200
  bg-white
  px-3.5 py-3
  text-sm
  text-slate-900
  outline-none
  transition
  placeholder:text-slate-400
  focus:border-red-500
  focus:ring-4
  focus:ring-red-500/10
  dark:border-slate-700
  dark:bg-slate-950
  dark:text-slate-100
  dark:placeholder:text-slate-500
`;

const INITIAL_FORM = {
  nom_cours: "",
  theme_cours: "",
  description_cours: "",
  date_cours: "",
  heure_debut: "",
  heure_fin: "",
  lieu: "",
  enseignants_ids: [],
  assistants_ids: [],
};

const PersonSelector = ({
  title,
  icon: Icon,
  people = [],
  selectedIds = [],
  onToggle,
}) => (
  <div>
    <div className="mb-2 flex items-center gap-2">
      <Icon className="text-red-500" />

      <label className="text-sm font-semibold text-slate-800 dark:text-slate-100">
        {title}
      </label>

      {selectedIds.length > 0 && (
        <span className="ml-auto rounded-full bg-red-50 px-2 py-0.5 text-xs font-bold text-red-700 dark:bg-red-950/40 dark:text-red-300">
          {selectedIds.length}
        </span>
      )}
    </div>

    <div
      className="
        grid max-h-64 grid-cols-1
        gap-2 overflow-y-auto
        rounded-2xl
        border border-slate-200
        bg-slate-50
        p-2
        dark:border-slate-800
        dark:bg-slate-900/70
        sm:grid-cols-2
      "
    >
      {people.length > 0 ? (
        people.map((person) => {
          const selected =
            selectedIds.includes(
              Number(person.id)
            );

          return (
            <label
              key={person.id}
              className={`
                flex cursor-pointer
                items-center gap-3
                rounded-xl
                border
                p-3
                transition
                ${
                  selected
                    ? "border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950/30"
                    : "border-slate-200 bg-white hover:border-slate-300 dark:border-slate-700 dark:bg-slate-950 dark:hover:border-slate-600"
                }
              `}
            >
              <input
                type="checkbox"
                checked={selected}
                onChange={() =>
                  onToggle(
                    Number(person.id)
                  )
                }
                className="h-4 w-4 shrink-0 accent-red-600"
              />

              <span className="min-w-0 truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                {getPersonName(person)}
              </span>
            </label>
          );
        })
      ) : (
        <p className="col-span-full p-4 text-center text-sm text-slate-400">
          Aucun élément disponible.
        </p>
      )}
    </div>
  </div>
);

const CoursAgendaFormDrawer = ({
  open,
  mode = "create",

  cours,
  enseignants = [],
  assistants = [],

  onClose,
  onSubmit,

  saving = false,
}) => {
  const [formData, setFormData] =
    useState(INITIAL_FORM);

  const [formError, setFormError] =
    useState("");

  const isEdit = mode === "edit";

  const isDuplicate =
    mode === "duplicate";

  useEffect(() => {
    if (!open) return;

    setFormError("");

    if (cours && (isEdit || isDuplicate)) {
      setFormData({
        nom_cours:
          cours.nom_cours || "",

        theme_cours:
          cours.theme_cours || "",

        description_cours:
          cours.description_cours || "",

        date_cours: isDuplicate
          ? addDaysToYmd(
              cours.date_cours,
              7
            )
          : toDateInputValue(
              cours.date_cours
            ),

        heure_debut:
          toTimeInputValue(
            cours.heure_debut
          ),

        heure_fin:
          toTimeInputValue(
            cours.heure_fin
          ),

        lieu: cours.lieu || "",

        enseignants_ids: (
          cours.enseignants_ids || []
        ).map(Number),

        assistants_ids: (
          cours.assistants_ids || []
        ).map(Number),
      });

      return;
    }

    setFormData({
      ...INITIAL_FORM,
      date_cours: ymdLocal(
        new Date()
      ),
    });
  }, [
    open,
    cours,
    isEdit,
    isDuplicate,
  ]);

  const title = useMemo(() => {
    if (isEdit) return "Modifier le cours";

    if (isDuplicate) {
      return "Dupliquer le cours";
    }

    return "Ajouter un cours";
  }, [isEdit, isDuplicate]);

  const subtitle = useMemo(() => {
    if (isDuplicate) {
      return "Le cours est prérempli avec une date décalée de 7 jours.";
    }

    if (isEdit) {
      return "Modifiez les informations puis enregistrez.";
    }

    return "Renseignez les informations du nouveau cours.";
  }, [isEdit, isDuplicate]);

  const updateField = (
    field,
    value
  ) => {
    setFormData((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const toggleId = (
    field,
    personId
  ) => {
    setFormData((previous) => {
      const ids =
        previous[field] || [];

      const exists =
        ids.includes(personId);

      return {
        ...previous,
        [field]: exists
          ? ids.filter(
              (id) => id !== personId
            )
          : [...ids, personId],
      };
    });
  };

  const handleSubmit = async (
    event
  ) => {
    event.preventDefault();

    setFormError("");

    if (
      !formData.nom_cours.trim()
    ) {
      setFormError(
        "Le nom du cours est obligatoire."
      );

      return;
    }

    if (!formData.date_cours) {
      setFormError(
        "La date du cours est obligatoire."
      );

      return;
    }

    if (
      !formData.heure_debut ||
      !formData.heure_fin
    ) {
      setFormError(
        "Les heures de début et de fin sont obligatoires."
      );

      return;
    }

    if (
      formData.heure_fin <=
      formData.heure_debut
    ) {
      setFormError(
        "L'heure de fin doit être postérieure à l'heure de début."
      );

      return;
    }

    if (!formData.lieu.trim()) {
      setFormError(
        "Le lieu est obligatoire."
      );

      return;
    }

    const payload = {
      nom_cours:
        formData.nom_cours.trim(),

      theme_cours:
        formData.theme_cours.trim(),

      description_cours:
        formData.description_cours.trim(),

      date_cours:
        formData.date_cours,

      heure_debut:
        formData.heure_debut,

      heure_fin:
        formData.heure_fin,

      lieu: formData.lieu.trim(),

      enseignants_ids:
        formData.enseignants_ids,

      assistants_ids:
        formData.assistants_ids,
    };

    await onSubmit?.(payload);
  };

  const footer = (
    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
      <button
        type="button"
        onClick={onClose}
        disabled={saving}
        className="
          rounded-xl
          border border-slate-200
          px-5 py-3
          text-sm font-semibold
          text-slate-700
          transition
          hover:bg-slate-100
          disabled:opacity-50
          dark:border-slate-700
          dark:text-slate-200
          dark:hover:bg-slate-800
        "
      >
        Annuler
      </button>

      <button
        type="submit"
        form="cours-agenda-form"
        disabled={saving}
        className="
          flex items-center justify-center
          gap-2
          rounded-xl
          bg-red-600
          px-5 py-3
          text-sm font-semibold
          text-white
          shadow-sm
          transition
          hover:bg-red-700
          disabled:cursor-not-allowed
          disabled:opacity-50
        "
      >
        <FaSave />

        {saving
          ? "Enregistrement..."
          : isEdit
          ? "Enregistrer les modifications"
          : isDuplicate
          ? "Créer la copie"
          : "Créer le cours"}
      </button>
    </div>
  );

  return (
    <CoursAgendaDrawerShell
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      footer={footer}
      widthClass="max-w-3xl"
    >
      <form
        id="cours-agenda-form"
        onSubmit={handleSubmit}
        className="space-y-6"
      >
        {/* Erreur */}
        {formError && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
            {formError}
          </div>
        )}

        {/* Informations */}
        <div>
          <h3 className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
            Informations générales
          </h3>

          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-200">
                Nom du cours *
              </label>

              <input
                type="text"
                value={formData.nom_cours}
                onChange={(event) =>
                  updateField(
                    "nom_cours",
                    event.target.value
                  )
                }
                placeholder="Ex. Krav Maga Adultes"
                className={CONTROL_CLASS}
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-200">
                Thème
              </label>

              <input
                type="text"
                value={
                  formData.theme_cours
                }
                onChange={(event) =>
                  updateField(
                    "theme_cours",
                    event.target.value
                  )
                }
                placeholder="Ex. Défense contre saisies"
                className={CONTROL_CLASS}
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-200">
                Description
              </label>

              <textarea
                value={
                  formData.description_cours
                }
                onChange={(event) =>
                  updateField(
                    "description_cours",
                    event.target.value
                  )
                }
                placeholder="Informations complémentaires sur le cours..."
                rows={4}
                className={`${CONTROL_CLASS} resize-y`}
              />
            </div>
          </div>
        </div>

        {/* Date / horaires */}
        <div>
          <h3 className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
            Date et horaires
          </h3>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <label className="mb-1.5 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                <FaCalendarAlt className="text-red-500" />
                Date *
              </label>

              <input
                type="date"
                value={
                  formData.date_cours
                }
                onChange={(event) =>
                  updateField(
                    "date_cours",
                    event.target.value
                  )
                }
                className={CONTROL_CLASS}
              />
            </div>

            <div>
              <label className="mb-1.5 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                <FaClock className="text-red-500" />
                Début *
              </label>

              <input
                type="time"
                value={
                  formData.heure_debut
                }
                onChange={(event) =>
                  updateField(
                    "heure_debut",
                    event.target.value
                  )
                }
                className={CONTROL_CLASS}
              />
            </div>

            <div>
              <label className="mb-1.5 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                <FaClock className="text-red-500" />
                Fin *
              </label>

              <input
                type="time"
                value={
                  formData.heure_fin
                }
                onChange={(event) =>
                  updateField(
                    "heure_fin",
                    event.target.value
                  )
                }
                className={CONTROL_CLASS}
              />
            </div>
          </div>
        </div>

        {/* Lieu */}
        <div>
          <label className="mb-1.5 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
            <FaMapMarkerAlt className="text-red-500" />
            Lieu *
          </label>

          <input
            type="text"
            value={formData.lieu}
            onChange={(event) =>
              updateField(
                "lieu",
                event.target.value
              )
            }
            placeholder="Salle, dojo, gymnase..."
            className={CONTROL_CLASS}
          />
        </div>

        {/* Encadrement */}
        <div>
          <h3 className="mb-4 text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
            Encadrement du cours
          </h3>

          <div className="space-y-5">
            <PersonSelector
              title="Instructeurs"
              icon={FaUser}
              people={enseignants}
              selectedIds={
                formData.enseignants_ids
              }
              onToggle={(id) =>
                toggleId(
                  "enseignants_ids",
                  id
                )
              }
            />

            <PersonSelector
              title="Assistants"
              icon={FaUserFriends}
              people={assistants}
              selectedIds={
                formData.assistants_ids
              }
              onToggle={(id) =>
                toggleId(
                  "assistants_ids",
                  id
                )
              }
            />
          </div>
        </div>
      </form>
    </CoursAgendaDrawerShell>
  );
};

export default CoursAgendaFormDrawer;